import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EvaluationUi from "./EvaluationUi";
import EvaluationHistory from "./EvaluationHistory";
import { useEvaluationForm } from "./useEvaluationForm";
import { Toaster } from "react-hot-toast";
import { getRoleOptions } from "./roleConfig";
import { HistoryIcon, ArrowRightIcon } from "../../Icons";
import { isRoleExcludedFromEvaluationModule } from "../../constants";
import { resolveHrDocumentReturnPath } from "../../utils/applicationFormNavigation";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_ACTIONS,
} from "../../ui/pageChrome";
import {
  EVALUATION_THEME,
  EVALUATION_BTN,
  EVALUATION_ROLE_ICON,
} from "./evaluationTheme";

const EvaluationNew = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Helper to parse URL parts correctly
  const parseUrl = React.useCallback(() => {
    const pathname = window.location.pathname;
    const parts = pathname.split("/").filter(Boolean);
    // e.g. ["evaluation", "rn", "edit", "10"]
    if (parts.length >= 2 && parts[0] === "evaluation") {
      const secondPart = parts[1].toLowerCase();

      if (secondPart === "history") {
        return { step: "HISTORY" };
      }

      const role = secondPart.toUpperCase();
      const action = parts[2];
      const id = parts[3];

      return {
        role,
        action,
        id,
        step: action === "edit" ? "EVALUATION_FORM" : "BASIC_INFO"
      };
    }
    return { step: "ROLE_SELECT" };
  }, []);

  const initialData = parseUrl();

  // Flow State
  const [currentStep, setCurrentStep] = useState(initialData.step);
  const [selectedRole, setSelectedRole] = useState(initialData.role || null);
  const [editingId, setEditingId] = useState(initialData.id || null);

  // Initialize form logic
  const {
    form,
    submit,
    submitData,
    isSubmitting,
    currentPage,
    setCurrentPage,
    signatureState,
    openESignModal,
    saveSignature,
    closeESignModal,
    loadEvaluation,
    isReadOnly, // Destructure isReadOnly
    isAdmin, // Destructure isAdmin
  } = useEvaluationForm(selectedRole, editingId);

  const { reset, trigger } = form;

  // Sync state and handle permissions in a single effect
  useEffect(() => {
    const data = parseUrl();

    if (data.role) {
      if (isRoleExcludedFromEvaluationModule(data.role)) {
        navigate("/evaluation");
        return;
      }
      // It's a deep link (edit or new)
      setSelectedRole(data.role);
      if (data.id) setEditingId(data.id);
      setCurrentStep(data.step);
    } else if (data.step === "HISTORY") {
      setCurrentStep("HISTORY");
      setSelectedRole(null);
      setEditingId(null);
    } else {
      // Handle base /evaluation route
      if (!data.role) {
        setCurrentStep("ROLE_SELECT");
        setSelectedRole(null);
        setEditingId(null);
      }

      if (isAdmin === false && data.step === "ROLE_SELECT") {
        navigate("/documents");
      }
    }
    // Sync from URL on pathname change only; omit currentStep to avoid re-running on every step change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAdmin]);

  // Get options from shared config
  const roleOptions = getRoleOptions();

  // Effect to load data when editingId changes
  // This needs to happen after selectedRole has been set and the form reset (which happens in hook useEffect)
  useEffect(() => {
    if (editingId && selectedRole) {
      // Small timeout to ensure hook's reset logic for role change has processed if any
      const timer = setTimeout(() => {
        loadEvaluation(editingId);
      }, 100);
      return () => clearTimeout(timer);
    }
    // Load when edit target is set; loadEvaluation is recreated each render in the form hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, selectedRole]);

  const handleRoleSelect = (roleValue) => {
    setSelectedRole(roleValue);
    setEditingId(null);
    setCurrentStep("BASIC_INFO");
  };

  const handleBasicInfoSubmit = async () => {
    // Validate basic info fields before proceeding
    const isValid = await trigger([
      "employeeName",
      "evaluationDate",
      "reviewType",
      "evaluatorName",
    ]);
    if (isValid) {
      setCurrentStep("EVALUATION_FORM");
    }
  };

  const handleBackToRoles = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const returnUrl = resolveHrDocumentReturnPath(
      queryParams,
      "evaluation-forms",
    );

    reset();
    setSelectedRole(null);
    setEditingId(null);

    // Leave the evaluation route first. Resetting to role select before
    // navigation sends non-admin users to the employee list and drops the tab.
    if (returnUrl) {
      navigate(returnUrl);
      return;
    }

    setCurrentStep("ROLE_SELECT");
    navigate("/evaluation");
  };

  const handleViewHistory = () => {
    navigate("/evaluation/history");
  };

  const handleEditEvaluation = (evaluation) => {
    navigate(`/evaluation/${evaluation.type.toLowerCase()}/edit/${evaluation.id}?returnUrl=/evaluation/history`);
  };

  return (
    <div className={PAGE_SHELL}>
      <Toaster position="top-right" />

      {/* STEP 1: ROLE SELECTION */}
      {currentStep === "ROLE_SELECT" && (
        <div className="w-full space-y-4">
          <div className={PAGE_HEADER_ROW}>
            <div>
              <h2
                className={`text-2xl font-bold ${EVALUATION_THEME.section.title}`}
              >
                Start Evaluation
              </h2>
              <p className={PAGE_SUBTITLE}>
                Select a role to begin a new performance evaluation.
              </p>
            </div>
            <div className={PAGE_ACTIONS}>
              <button
                type="button"
                onClick={handleViewHistory}
                className={EVALUATION_BTN.outline}
              >
                <HistoryIcon size={16} />
                View History
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {roleOptions.map((pos) => {
              const Icon = pos.icon;
              return (
                <button
                  key={pos.value}
                  type="button"
                  onClick={() => handleRoleSelect(pos.value)}
                  className={`bg-white p-5 rounded-xl shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-200 border text-left cursor-pointer group ${EVALUATION_THEME.section.wrap}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold leading-tight text-slate-900">
                        {pos.label}
                      </h3>
                      <div
                        className={`flex items-center gap-1.5 mt-3 text-sm font-medium opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 ${EVALUATION_THEME.button.link}`}
                      >
                        Start <ArrowRightIcon size={14} />
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded-lg shrink-0 transition-transform duration-200 group-hover:scale-105 ${EVALUATION_ROLE_ICON}`}
                    >
                      {React.isValidElement(Icon) ? (
                        Icon
                      ) : (
                        <Icon size={22} />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 4: HISTORY */}
      {currentStep === "HISTORY" && (
        <EvaluationHistory
          onBack={handleBackToRoles}
          onEdit={handleEditEvaluation}
        />
      )}

      {/* STEP 2 & 3: BASIC INFO & FORM */}
      {(currentStep === "BASIC_INFO" || currentStep === "EVALUATION_FORM") && (
        <EvaluationUi
          form={form}
          submit={submit}
          submitData={submitData} // Pass submitData to EvaluationUi
          isSubmitting={isSubmitting}
          currentStep={currentStep}
          onBasicInfoSubmit={handleBasicInfoSubmit}
          onCancel={handleBackToRoles}
          selectedRole={selectedRole}
          // Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          // Signature
          signatureState={signatureState}
          openESignModal={openESignModal}
          saveSignature={saveSignature}
          closeESignModal={closeESignModal}
          isReadOnly={isReadOnly}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

export default EvaluationNew;
